import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { specialsApi } from '@/api/endpoints/specials';
import { SpecialCard } from '@/components/domain';
import { SEOHead } from '@/components/seo';
import { PageSpinner, ButtonGroup, ToggleButton, LocationPermissionPrompt, LocationErrorPrompt, NoAreaCoverage } from '@/components/ui';
import { Beer, Utensils, Clock } from 'lucide-react';
import { isActiveOnDate } from '@/lib/rrule';
import { getLocationSEO } from '@/lib/seoUtils';
import { useFilters, metersToMiles } from '@/contexts/FilterContext';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useAreaCoverage } from '@/hooks/useAreaCoverage';

type SpecialType = 'drink' | 'food' | undefined;
type HappyHourFilter = 'all' | 'only' | 'exclude';

// Happy hour filter state labels
const hhFilterLabels: Record<HappyHourFilter, string> = {
  exclude: 'HH Off',
  all: 'All Specials',
  only: 'HH Only',
};

// Cycle order: exclude -> all -> only -> exclude
const hhFilterCycle: Record<HappyHourFilter, HappyHourFilter> = {
  exclude: 'all',
  all: 'only',
  only: 'exclude',
};

export function SpecialsPage() {
  const [type, setType] = useState<SpecialType>(undefined);
  const [hhFilter, setHhFilter] = useState<HappyHourFilter>('exclude');
  const { date, lat, lng, radius, hasValidLocation, locationStatus, locationLabel, locationSource } = useFilters();
  const { hidden } = useUserPreferences();
  const { hasBarCoverage, isCheckingCoverage } = useAreaCoverage();

  // Fetch all specials for the radius (no pagination, no date filter server-side)
  const {
    data,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['specials', { type, lat, lng, radius }],
    queryFn: () => specialsApi.list({ type, lat: lat!, lng: lng!, radius, limit: 500, offset: 0 }),
    enabled: hasValidLocation,
  });

  // All specials from API (already sorted by price server-side)
  const allSpecials = data?.specials ?? [];

  // Filter out hidden bars first, then by date client-side using RRULE
  let filteredSpecials = allSpecials.filter(
    (s) => !hidden.has(s.barId!) && isActiveOnDate(s.rrule, date)
  );

  // Filter by happy hour
  if (hhFilter === 'only') {
    filteredSpecials = filteredSpecials.filter((s) => s.isHappyHour);
  } else if (hhFilter === 'exclude') {
    filteredSpecials = filteredSpecials.filter((s) => !s.isHappyHour);
  }

  // Location guards
  if (!hasValidLocation) {
    if (locationStatus === 'denied') return <LocationPermissionPrompt />;
    if (locationStatus === 'unavailable') return <LocationErrorPrompt />;
    return <PageSpinner />;
  }

  if (isCheckingCoverage || isLoading) return <PageSpinner />;
  if (!hasBarCoverage) return <NoAreaCoverage />;

  if (isError) {
    return (
      <div className="py-12 text-center">
        <p className="text-[var(--color-muted)]">Failed to load specials</p>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        {...getLocationSEO(
          "Today's Bar Specials Near You",
          `Find ${filteredSpecials.length} bar specials near you today. Drink deals, food specials, and happy hours from local bars. Updated daily.`,
          '/specials',
          locationLabel,
          locationSource
        )}
      />
      <div className="space-y-4">
        {/* Filter buttons */}
      <div className="flex items-center justify-between gap-3">
        {/* Type filter - left aligned */}
        <ButtonGroup>
          <ToggleButton
            isActive={type === undefined}
            onClick={() => setType(undefined)}
          >
            All
          </ToggleButton>
          <ToggleButton
            isActive={type === 'drink'}
            onClick={() => setType('drink')}
          >
            <Beer className="h-4 w-4" />
            Drinks
          </ToggleButton>
          <ToggleButton
            isActive={type === 'food'}
            onClick={() => setType('food')}
          >
            <Utensils className="h-4 w-4" />
            Food
          </ToggleButton>
        </ButtonGroup>

        {/* Happy hour filter - right aligned, single cycling button styled like ButtonGroup */}
        <div className="inline-flex rounded-lg border border-border bg-card p-0.5">
          <button
            type="button"
            onClick={() => setHhFilter(hhFilterCycle[hhFilter])}
            className="px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 text-foreground hover:bg-border"
          >
            <Clock className={`h-4 w-4 ${hhFilter === 'only' ? 'text-accent-primary' : hhFilter === 'exclude' ? 'text-muted' : ''}`} />
            {hhFilterLabels[hhFilter]}
          </button>
        </div>
      </div>

      {/* Specials list */}
      {filteredSpecials.length === 0 ? (
        <div className="py-12 text-center">
          <Beer className="mx-auto h-12 w-12 text-[var(--color-muted)] opacity-50" />
          <p className="mt-4 text-[var(--color-muted)]">
            No specials found within {metersToMiles(radius)} miles
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filteredSpecials.map((special) => (
            <SpecialCard key={special.id} special={special} />
          ))}
        </div>
      )}
      </div>
    </>
  );
}
